import React, { useMemo, useState } from 'react';
import styles from './Metrics.module.scss';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import IncreaseArrow from '@/assets/Icons/Arrow-Increase.svg?react';
import DecreaseArrow from '@/assets/Icons/Arrow-Decrease.svg?react';
import { CountUp } from 'use-count-up';
import { isNumber } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { periodOptions } from '@/constants/climatePro.ts';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { getMyClimateMetrics } from '@/api/myClimate.ts';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import Select from '@/components/ui/Select/Select.tsx';
import Co2Period = App.Enums.Co2Period;
import { formatNumber } from '@/styles/utils/formatNumber.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';

const Metrics: React.FC = () => {
	const { organizationEmissionTarget } = useWhoamiStore();
	const [period, setPeriod] = useState<Co2Period>('last-12-months');

	const { data, isLoading } = useQuery({
		queryKey: [queryKeys.getMyClimateMetrics, period],
		queryFn: () => getMyClimateMetrics({ period }),
		enabled: !!period,
	});

	const availablePeriodOptions = useMemo(() => {
		return periodOptions.filter(
			option => !(option.value === 'current-period' && organizationEmissionTarget === null),
		);
	}, [organizationEmissionTarget]);

	return (
		<FlexBlock flexDirection="column" rowGap="24px" className={styles.myClimateMetricsWrapper}>
			<div className={styles.headerSection}>
				<FlexBlock justifyContent="space-between" alignItems="center" className={styles.titleRow}>
					<h1>CO2 metrics</h1>
					<FlexBlock columnGap="20px" className={styles.selectWrapper}>
						<Select
							required
							name="period"
							value={period}
							onChange={period => {
								setPeriod(period as Co2Period);
								trackEvent(productAnalyticsEvents.myClimate.metrics.overview.interact, {
									deduplicationKey: `period.${period}`,
									postHogEventName: 'changed_period_metrics',
									postHogProperties: { period, category: 'metrics' },
								});
							}}
							options={availablePeriodOptions}
							placeholder="Choose period"
							className={styles.selectDropdown}
						/>
					</FlexBlock>
				</FlexBlock>
				<h2 className={styles.subtitle}>See the emissions from your recent travel</h2>
			</div>
			<FlexBlock
				justifyContent="space-between"
				rowGap="32px"
				columnGap="24px"
				flexWrap="wrap"
				className={styles.metricsCards}
			>
				{!isLoading && data && data?.metrics?.length > 0 ? (
					data?.metrics.map((item, index) => (
						<div className={styles.metricsCard} key={index}>
							<h4>{item.label}</h4>
							<p className={styles.amount}>
								{isNumber(item.value) ? (
									<CountUp
										end={item.value}
										isCounting
										duration={0.75}
										formatter={value => formatNumber(value)}
									/>
								) : (
									item.value
								)}
							</p>
							{item.isIncreasing !== null && (
								<div className={styles.percentage}>
									<span className={item.isPositive ? styles.positive : styles.negative}>
										{item.isIncreasing ? <IncreaseArrow /> : <DecreaseArrow />}{' '}
										{item.percentageChange}%
									</span>{' '}
									period-over-period
								</div>
							)}
						</div>
					))
				) : (
					<>
						{Array.from({ length: 4 }).map((_, index) => (
							<div key={index} className={styles.metricsCard}>
								<Skeleton className={styles.skeletonTitle} />
								<Skeleton className={styles.skeletonValue} />
								<Skeleton className={styles.skeletonAddInfo} />
							</div>
						))}
					</>
				)}
			</FlexBlock>
		</FlexBlock>
	);
};

export default Metrics;
